import { formatDate, formatTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  DataTable,
  confirm,
  createSortableColumn,
  type TanstackReactTable,
} from "@devhop/ui";
import { CalendarIcon, EditIcon, EyeIcon, XIcon } from "lucide-react";
import type { ClassSessionDTO } from "../../domain/contracts/sessions";
import { formatSessionPlace } from "./session-display";

type ClassSessionRow = ClassSessionDTO;

type ClassSessionTableProps = {
  data: ClassSessionRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (session: ClassSessionRow) => void;
  onEdit: (session: ClassSessionRow) => void;
  onMove: (session: ClassSessionRow) => void;
  onCancel: (id: string) => void;
  onComplete?: (id: string) => void;
};

export function ClassSessionTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onMove,
  onCancel,
  onComplete,
}: ClassSessionTableProps) {
  const columns: TanstackReactTable.ColumnDef<ClassSessionRow>[] = [
    createSortableColumn("date", "ວັນທີ", {
      cell: ({ row }) => formatDate(row.original.date),
    }),
    {
      header: "ເວລາ",
      accessorKey: "startTime",
      cell: ({ row }) => (
        <span>
          {formatTime(row.original.startTime)} -{" "}
          {formatTime(row.original.endTime)}
        </span>
      ),
    },
    createSortableColumn("templateName", "ແມ່ແບບ", {
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.templateName}</div>
          <div className="text-muted-foreground text-xs">
            {row.original.classTypeName} - {row.original.levelName}
          </div>
        </div>
      ),
    }),
    createSortableColumn("roomName", "ສະຖານທີ່", {
      cell: ({ row }) => formatSessionPlace(row.original),
    }),
    createSortableColumn("instructorNames", "ຄູຜູ້ສອນ", {
      cell: ({ row }) =>
        Array.isArray(row.original.instructorNames)
          ? row.original.instructorNames.join(", ") || "-"
          : "-",
    }),
    {
      header: "ຄວາມຈຸ",
      accessorKey: "capacity",
      cell: ({ row }) => (
        <span>
          {row.original.attendanceCount ?? 0} / {row.original.capacity}
        </span>
      ),
    },
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status;
        const variants: Record<
          typeof status,
          "default" | "secondary" | "destructive"
        > = {
          scheduled: "default",
          completed: "secondary",
          cancelled: "destructive",
        };
        const labels: Record<typeof status, string> = {
          scheduled: "ຈອງແລ້ວ",
          completed: "ເຂົ້າຮຽນແລ້ວ",
          cancelled: "ຍົກເລີກ",
        };
        return <Badge variant={variants[status]}>{labels[status]}</Badge>;
      },
    },
    {
      header: "ໝາຍເຫດ",
      accessorKey: "notes",
      cell: ({ row }) => (
        <div className="max-w-md truncate">{row.original.notes || "-"}</div>
      ),
    },
    createSortableColumn("createdAt", "ວັນທີສ້າງ", {
      cell: ({ row }) => formatDate(row.original.createdAt),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }: { row: { original: ClassSessionRow } }) => {
        const session = row.original;
        const actions = [];

        actions.push({
          label: "ເບິ່ງລາຍລະອຽດ",
          icon: <EyeIcon className="h-4 w-4" />,
          onClick: () => onView(session),
        });

        if (session.status === "scheduled") {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(session),
          });

          // actions.push({
          //   label: "ຍ້າຍ",
          //   icon: <MoveIcon className="h-4 w-4" />,
          //   onClick: () => onMove(session),
          // });

          if (onComplete) {
            actions.push({
              label: "ສຳເລັດ",
              icon: <CalendarIcon className="h-4 w-4" />,
              onClick: async () => {
                const ok = await confirm({
                  title: "ສຳເລັດຊົ່ວໂມງຮຽນ",
                  description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະສຳເລັດ ຊົ່ວໂມງຮຽນ ນີ້?",
                  actionText: "ສຳເລັດ",
                });
                if (ok) onComplete(session.id);
              },
            });
          }

          actions.push({
            label: "ຍົກເລີກ",
            variant: "destructive" as const,
            icon: <XIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ຍົກເລີກຊົ່ວໂມງຮຽນ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກ ຊົ່ວໂມງຮຽນ ນີ້?",
                actionText: "ຍົກເລີກ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) onCancel(session.id);
            },
          });
        }

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<ClassSessionRow, unknown>
      noDataMessage="ບໍ່ພົບ ຊົ່ວໂມງຮຽນ ຄລາດສ໌"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
